import { debounce } from 'lodash';
import React, { useState } from 'react';

import Question from 'Containers/ProfilePage/_shared/Question';
import { ClaimsSectionInfoBar } from 'Containers/ProfilePage/ClaimsSection/ClaimsSectionInfoBar';
import TypeaheadSearch from 'Containers/ProfilePage/MemberSection/HouseholdMember/PrescriptionForm/TypeaheadSearch';
import { Suggestion } from 'Containers/ProfilePage/MemberSection/HouseholdMember/PrescriptionForm/TypeaheadSearch/SuggestionsList';
import { AudioConfigTooltipContentfulPaths } from 'Containers/ProfilePage/ProfileAudio';
import { ClaimsFormData, ClaimsFormStatus, ClaimsPayer } from 'Containers/ProfilePage/types';
import { ClaimsCard, ProfileAudio } from 'ContentfulDefaults/types/profile';
import { ContentfulTextNote } from 'ContentfulWrappers/ContentfulTextNote';
import { useProfileContext } from 'Contexts/profileContext';
import { useTextContext } from 'Contexts/textContext';
import { Button, Icon, Paragraph, Spinner, Tooltip } from 'DesignLibrary/atoms';
import { AudioTooltip } from 'DesignLibrary/atoms/AudioTooltip';
import { DateOfBirthInput, isValidDob } from 'DesignLibrary/atoms/inputs/DateOfBirthInput';
import TextInput from 'DesignLibrary/atoms/inputs/TextInput';
import ToggleList from 'DesignLibrary/atoms/ToggleList';
import { sendInsightsEvent } from 'Utils/insights';

import {
  ClaimsButtonContainer,
  ClaimsFormContainer,
  ClaimsOuter,
  ClaimsPayerContainer,
  MemberIdLabelContainer,
  SpinnerContainer,
} from './styled';

export interface ClaimsSectionProps {
  isProfileAudioEnabled: boolean;
  claimsFormData: ClaimsFormData;
  claimsFormStatus: ClaimsFormStatus;
  updateClaimsFormData: (field: string, value: string | boolean | ClaimsPayer | null) => void;
  updateClaimsFormStatus: (state: ClaimsFormStatus) => void;
  queryClaimsPayers: (query: string) => void;
  claimsPayers: ClaimsPayer[];
  claimsPayersLoading: boolean;
  claimsSummaryPolling: boolean;
  submitClaimsForm: (data: ClaimsFormData) => void;
  handleSectionChange: (isValid: boolean, isComplete: boolean) => void;
  handleSkipClaimsForm: () => void;
  isClaimsFeatureEnabled?: boolean;
  planYear?: number | null;
  claimsErrorState?: string | null;
}

export const ClaimsSection = ({
  isProfileAudioEnabled,
  claimsFormData,
  claimsFormStatus,
  updateClaimsFormData,
  updateClaimsFormStatus,
  queryClaimsPayers,
  claimsPayers,
  claimsPayersLoading,
  claimsSummaryPolling,
  submitClaimsForm,
  handleSectionChange,
  handleSkipClaimsForm,
  isClaimsFeatureEnabled = false,
  planYear = null,
  claimsErrorState,
}: ClaimsSectionProps) => {
  const { getSectionFromMap, getIsDirty } = useProfileContext();
  const { retrieveContentfulData } = useTextContext();

  const validateField = (
    field: keyof ClaimsFormData,
    value: string | boolean | ClaimsPayer | null,
  ): boolean => {
    switch (field) {
      case 'dateOfBirth': {
        return isValidDob(value?.toString() || '');
      }
      case 'claimsPayer': {
        if (value === null) return false;
        const payer = value as ClaimsPayer;
        return !!(payer.name && payer.payerId);
      }
      case 'isPolicyholder':
        return value !== null;
      default:
        return (value?.toString().length || 0) > 1;
    }
  };

  const [formValidStatus, setFormValidStatus] = useState<Record<keyof ClaimsFormData, boolean | null>>({
    firstName: claimsFormData.firstName ? validateField('firstName', claimsFormData.firstName) : null,
    lastName: claimsFormData.lastName ? validateField('lastName', claimsFormData.lastName) : null,
    dateOfBirth: claimsFormData.dateOfBirth ? validateField('dateOfBirth', claimsFormData.dateOfBirth) : null,
    claimsPayer: claimsFormData.claimsPayer ? validateField('claimsPayer', claimsFormData.claimsPayer) : null,
    memberId: claimsFormData.memberId ? validateField('memberId', claimsFormData.memberId) : null,
    isPolicyholder:
      claimsFormData.isPolicyholder !== null
        ? validateField('isPolicyholder', claimsFormData.isPolicyholder)
        : null,
  });

  const [formDirtyStatus, setFormDirtyStatus] = useState<Record<keyof ClaimsFormData, boolean>>({
    firstName: false,
    lastName: false,
    dateOfBirth: false,
    claimsPayer: false,
    memberId: false,
    isPolicyholder: false,
  });

  const formValid = Object.values(formValidStatus).every((status) => status);
  const formLoading = claimsFormStatus === ClaimsFormStatus.Loading || claimsSummaryPolling;

  const isIncomplete = !getSectionFromMap('claims')?.isComplete;
  const isDirty = getIsDirty('claims');

  const contentfulCard = retrieveContentfulData<ClaimsCard>(`profile_section.claims`);
  const memberIdAudioConfig: ProfileAudio = retrieveContentfulData<ProfileAudio>(
    AudioConfigTooltipContentfulPaths.ClaimsSectionMemberIdAudioTooltip,
  );

  const handleStart = () => {
    const formComplete = Object.values(formValidStatus).every((status) => status === true);
    handleSectionChange(formComplete, formComplete);
    updateClaimsFormStatus(ClaimsFormStatus.Started);
  };

  const handleSubmit = () => {
    submitClaimsForm(claimsFormData);
    sendInsightsEvent(null, 'claims_form_submitted');
  };

  const handleEdit = () => {
    updateClaimsFormStatus(ClaimsFormStatus.Started);
  };

  const markDirty = (field: string) => {
    setFormDirtyStatus({
      ...formDirtyStatus,
      [field]: true,
    });
  };

  const handleChange = (field: string, value: string | boolean | ClaimsPayer | null) => {
    const isValid = validateField(field as keyof ClaimsFormData, value);
    markDirty(field);

    const updatedFormValidStatus = { ...formValidStatus, [field]: isValid };
    const formComplete = Object.values(updatedFormValidStatus).every((status) => status);
    setFormValidStatus(updatedFormValidStatus);
    handleSectionChange(formComplete, formComplete);
    updateClaimsFormData(field, value);
  };

  const handleSearchClaimsPayers = debounce((query: string) => {
    queryClaimsPayers(query);
  }, 250);

  const showForm =
    claimsFormStatus !== ClaimsFormStatus.Default &&
    claimsFormStatus !== ClaimsFormStatus.Skipped &&
    claimsFormStatus !== ClaimsFormStatus.Success;

  const formatClaimsPayers = (): Suggestion[] => {
    if (!claimsPayers || !claimsPayers.length) {
      return [];
    }

    return claimsPayers.map((payer) => ({
      key: payer.payerId,
      title: payer.name,
      description: '',
    }));
  };

  const claimsFormNoteFields = contentfulCard.text_notes?.find(
    (n) => planYear !== null && +n.plan_year === planYear,
  );

  return (
    <ClaimsOuter id="claims-section" data-testid="claims-section">
      <Question
        error={isDirty && !formValid}
        name="claims"
        isIncomplete={isIncomplete}
        audioTooltipsEnabled={isProfileAudioEnabled}
        audioTooltipContentfulPath={AudioConfigTooltipContentfulPaths.ClaimsSectionAudioTooltip}
      >
        <Paragraph>{contentfulCard.member_id_description}</Paragraph>
        <ClaimsButtonContainer className="claims-button-container">
          {claimsFormStatus === ClaimsFormStatus.Success ? (
            <Button data-testid="claims-edit" onClick={handleEdit}>
              {contentfulCard.edit_button}
            </Button>
          ) : (
            <>
              <Button
                data-testid="claims-start"
                className={claimsFormStatus === ClaimsFormStatus.Started ? 'selected' : ''}
                onClick={handleStart}
                disabled={formLoading}
              >
                {contentfulCard.start_button}
              </Button>
              <Button
                data-testid="claims-skip-top"
                className={claimsFormStatus === ClaimsFormStatus.Skipped ? 'selected' : ''}
                onClick={handleSkipClaimsForm}
                disabled={formLoading}
              >
                {contentfulCard.skip_button}
              </Button>
            </>
          )}
        </ClaimsButtonContainer>
        {showForm && (
          <>
            {claimsFormNoteFields &&
              (claimsFormNoteFields.long_text || claimsFormNoteFields.short_text) &&
              isClaimsFeatureEnabled &&
              claimsFormNoteFields?.short_text && (
                <ContentfulTextNote data-testid="text-note" note={claimsFormNoteFields} />
              )}
            <hr />
            <ClaimsFormContainer className="claims-form-container">
              <Paragraph id="claims-first-name-label" data-testid="claims-first-name-label">
                {contentfulCard.first_name}
              </Paragraph>
              <TextInput
                data-testid="claims-first-name-input"
                stretch
                handleChange={(e) => handleChange('firstName', e.target.value)}
                value={claimsFormData.firstName || ''}
                error={formDirtyStatus.firstName && !formValidStatus.firstName}
                disabled={formLoading}
              />
              <Paragraph id="claims-last-name-label">{contentfulCard.last_name}</Paragraph>
              <TextInput
                data-testid="claims-last-name-input"
                stretch
                handleChange={(e) => handleChange('lastName', e.target.value)}
                value={claimsFormData.lastName || ''}
                error={formDirtyStatus.lastName && !formValidStatus.lastName}
                disabled={formLoading}
              />
              <Paragraph id="claims-dob-label">{contentfulCard.date_of_birth}</Paragraph>
              <DateOfBirthInput
                stretch
                onChange={(dob) => handleChange('dateOfBirth', dob)}
                error={formDirtyStatus.dateOfBirth && !formValidStatus.dateOfBirth}
                value={claimsFormData.dateOfBirth || ''}
                disabled={formLoading}
              />
              <Paragraph id="claims-insurance-carrier-label">{contentfulCard.insurance_carrier}</Paragraph>
              {claimsFormData.claimsPayer ? (
                <ClaimsPayerContainer>
                  <Paragraph id="claims-insurance-carrier-value">{claimsFormData.claimsPayer.name}</Paragraph>
                  <Button
                    data-testid="claims-payer-remove"
                    onClick={() => handleChange('claimsPayer', null)}
                    iconRight="X"
                    disabled={formLoading}
                  />
                </ClaimsPayerContainer>
              ) : (
                <TypeaheadSearch
                  isLoading={claimsPayersLoading}
                  suggestions={formatClaimsPayers()}
                  placeholder={contentfulCard.search_placeholder}
                  isDisabled={formLoading}
                  isMemberComplete
                  labelId="claims-payer-label"
                  searchKey="carrier"
                  noResultsField="profile_section.claims.insurance_carrier_no_results"
                  errorTextField="profile_section.error_text.invalidCarrierInput"
                  queryResults={(value) => handleSearchClaimsPayers(value)}
                  handleSelectResult={(key: string, displayName: string) => {
                    handleChange('claimsPayer', { name: displayName, payerId: key });
                  }}
                />
              )}
              <Paragraph id="claims-member-id-label">
                <MemberIdLabelContainer>
                  {isProfileAudioEnabled && (
                    <div className="member-id-tooltip">
                      <AudioTooltip
                        data-testid="audio-tooltip-member-id"
                        urlToAudio={memberIdAudioConfig.audio_file_url}
                        urlToClosedCaptionVtt={memberIdAudioConfig.vtt_transcript_file_url}
                      />
                    </div>
                  )}
                  {contentfulCard.member_id}
                  {!isProfileAudioEnabled && (
                    <Tooltip
                      id="member-id-tooltip"
                      data-testid="member-id-tooltip"
                      label={contentfulCard.tooltips.memberId || ''}
                    >
                      <div className="icon">
                        <Icon type="Question" color="--text-black" />
                      </div>
                    </Tooltip>
                  )}
                </MemberIdLabelContainer>
              </Paragraph>
              <TextInput
                stretch
                data-testid="claims-member-id-input"
                handleChange={(e) => handleChange('memberId', e.target.value)}
                value={claimsFormData.memberId || ''}
                error={formDirtyStatus.memberId && !formValidStatus.memberId}
                className="medium-width"
                disabled={formLoading}
              />
              <Paragraph id="claims-policyholder-label">{contentfulCard.policyholder_question}</Paragraph>
              <div className="claims-policyholder-options">
                <ToggleList
                  color="--colors-gray-black"
                  label={contentfulCard.policyholder_question}
                  groupName="claims-policyholder-options"
                  options={[
                    { value: 'true', option: contentfulCard.policyholder_question_yes },
                    { value: 'false', option: contentfulCard.policyholder_question_no },
                  ]}
                  selected={
                    claimsFormData.isPolicyholder === null
                      ? undefined
                      : claimsFormData.isPolicyholder
                        ? 'true'
                        : 'false'
                  }
                  handleChange={(e) => handleChange('isPolicyholder', e.target.value === 'true')}
                  disabled={formLoading}
                />
              </div>
            </ClaimsFormContainer>
            <ClaimsButtonContainer>
              <Button
                data-testid="claims-submit"
                onClick={handleSubmit}
                buttonType="primary"
                disabled={!formValid || formLoading}
                className={formLoading ? 'loading-submit-button' : ''}
              >
                <>
                  {formLoading && (
                    <SpinnerContainer>
                      <Spinner color="--colors-gray-black" />
                    </SpinnerContainer>
                  )}
                  <div className={formLoading ? 'hidden' : ''}>{contentfulCard.submit_button}</div>
                </>
              </Button>
              <Button data-testid="claims-skip-bottom" onClick={handleSkipClaimsForm} disabled={formLoading}>
                {contentfulCard.skip_button}
              </Button>
            </ClaimsButtonContainer>
          </>
        )}
        <ClaimsSectionInfoBar
          status={claimsFormStatus}
          contentfulCard={contentfulCard}
          claimsErrorState={claimsErrorState}
        />
      </Question>
    </ClaimsOuter>
  );
};
